import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

const FileUpload = ({ onFilesSelected, maxFiles = 5, maxSize = 10 * 1024 * 1024 }) => {
  const { t } = useTranslation();
  const [error, setError] = useState('');

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError('');

    // Show a warning for rejected files (wrong type or too large), but still process accepted ones
    if (rejectedFiles.length > 0) {
      const firstRejected = rejectedFiles[0];
      const firstError = firstRejected?.errors?.[0];
      if (firstError?.code === 'file-too-large') {
        setError(t('fileUpload.errors.fileTooLarge'));
      } else if (firstError?.code === 'file-invalid-type') {
        setError(t('fileUpload.errors.invalidFileType'));
      } else {
        setError(t('fileUpload.errors.unknown'));
      }
      // Continue processing accepted files below — don't return
    }

    // If more files were selected than the per-batch limit, clip and warn
    let filesToProcess = acceptedFiles;
    if (acceptedFiles.length > maxFiles) {
      filesToProcess = acceptedFiles.slice(0, maxFiles);
      setError(t('fileUpload.errors.tooManyFiles', { max: maxFiles }));
    }

    if (filesToProcess.length > 0) {
      onFilesSelected(filesToProcess);
    }
  }, [maxFiles, maxSize, onFilesSelected, t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxSize,
    multiple: true
  });

  const acceptedTypesArray = Object.values(getRootProps().accept || {}).flat().map(type => type.startsWith('.') ? type.substring(1).toUpperCase() : type);
  const acceptedTypesString = acceptedTypesArray.join(', ');
  const maxSizeMB = maxSize / 1024 / 1024;

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}`}
      >
        <input {...getInputProps()} />
        <div className="space-y-2">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="text-sm text-gray-600">
            {isDragActive ? (
              <p>{t('fileUpload.dropHere')}</p>
            ) : (
              <p>
                {t('fileUpload.dragAndDrop')} <br />
                {t('fileUpload.orClickToSelect')}
              </p>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {t('fileUpload.supportedFormatsDetails', { maxFiles, acceptedTypesString, maxSizeMB })}
          </p>
        </div>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default FileUpload; 